import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { Gender } from '@core/enums/gender.enum';
import { Industry, User } from '@core/models/user.model';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { DeepLinkService } from '@core/services/deep-link.service';
import { EnvironmentService } from '@core/services/environment.service';
import { NzGlobalConfigService } from '@core/services/nz-global-config.service';
import { ImpersonationContext, StoredImpersonationContext } from '@core/types/impersonation.types';
import { performCommonTasks } from '@core/utils/app-initializer-utilities/core-tasks';
import { AdpPlatformTokenClaims } from '@core/utils/user-utilities';
import { Language, LanguageMap } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import { LoggerService, StorageService } from '@kerberos-compliance/compliance-shared-ui';
import { jwtDecode } from 'jwt-decode';
import { AuthFacade } from '@/auth/facades/auth.facade';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly storage = inject(StorageService);

  private readonly logger = inject(LoggerService);

  private readonly navigationService = inject(NavigationService);

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly deepLinkService = inject(DeepLinkService);

  private readonly nzGlobalConfigService = inject(NzGlobalConfigService);

  private readonly environmentService = inject(EnvironmentService);

  private readonly injector = inject(Injector);

  private readonly impersonatedDisplayName = signal<string>('');

  private readonly impersonatedEmail = signal<string>('');

  private readonly impersonatedReturnToken = signal<string>('');

  private readonly active = signal<boolean>(false);

  public constructor() {
    this.rehydrateFromStorage();

    effect(() => {
      this.nzGlobalConfigService.notificationPositionWhenBannerVisible(!!this.bannerMessage());
    });
  }

  public readonly bannerMessage = computed<string>(() => {
    if (!this.active()) {
      return '';
    }
    const name = this.impersonatedDisplayName();
    const email = this.impersonatedEmail();
    if (name && email) {
      return `Impersonating: ${name} (${email})`;
    }
    if (name) {
      return `Impersonating: ${name}`;
    }
    if (email) {
      return `Impersonating: ${email}`;
    }
    return 'Impersonating session is active';
  });

  public readonly context = computed<ImpersonationContext>(() => ({
    displayName: this.impersonatedDisplayName() || undefined,
    email: this.impersonatedEmail() || undefined,
    returnToken: this.impersonatedReturnToken() || undefined,
    active: this.active(),
  }));

  /**
   * Attempts to initialize the impersonation session based on URL query parameters.
   * Extracts specific parameters such as `impersonationToken`, `impersonationRefreshToken`,
   * `impersonatedDisplayName`, `impersonatedUserEmail`, and `returnToken` to set up
   * the impersonation context, persist tokens, update application state,
   * and clean up the URL by removing authentication-related query parameters.
   *
   * @returns {Promise<boolean>} Resolves to true when impersonation is initialized, false otherwise.
   */
  public async initializeFromUrl(): Promise<boolean> {
    try {
      this.logger.debug('[ImpersonationService] initializeFromUrl: starting');
      const url = new URL(globalThis.location.href);
      const parameters = url.searchParams;
      const accessToken = parameters.get('impersonationToken') ?? '';
      const refreshToken = parameters.get('impersonationRefreshToken') ?? '';
      const displayName = parameters.get('impersonatedDisplayName') ?? '';
      const email = parameters.get('impersonatedUserEmail') ?? '';
      const returnToken = parameters.get('returnToken') ?? parameters.get('impersonationReturnToken') ?? '';
      this.logger.debug(
        `[ImpersonationService] initializeFromUrl: token present=${Boolean(accessToken)}, refresh=${Boolean(refreshToken)}, displayName=${Boolean(displayName)}, email=${Boolean(email)}, returnToken=${Boolean(returnToken)}.`,
      );
      if (!accessToken) {
        this.logger.debug('[ImpersonationService] initializeFromUrl: no impersonationToken present');
        return false;
      }

      // Persist tokens so the app can use Kerberos directly
      this.storage.set(StorageServiceKeysMap.AccessTokenKerberos, accessToken);
      if (refreshToken) {
        this.storage.set(StorageServiceKeysMap.RefreshTokenKerberos, refreshToken);
      }

      this.logger.debug('[ImpersonationService] initializeFromUrl: tokens persisted');

      // Mark impersonation active before dispatching any actions that trigger HTTP calls
      this.impersonatedDisplayName.set(displayName);
      this.impersonatedEmail.set(email);
      this.impersonatedReturnToken.set(returnToken);
      this.active.set(true);
      this.storage.set(StorageServiceKeysMap.IsImpersonated, true);

      // Persist the banner context
      this.persistContext({
        impersonationActive: true,
        displayName: displayName || undefined,
        email: email || undefined,
        returnToken: returnToken || undefined,
      });

      // The impersonation token IS the platform token — there is no identity
      // provider session behind it, which is why external auth is bypassed.
      const authFacade = this.authFacade;
      authFacade.updateTokens({
        accessTokenKerberos: accessToken,
        refreshTokenKerberos: refreshToken || null,
      });
      void authFacade.loadGrants(accessToken);

      this.logger.debug('[ImpersonationService] initializeFromUrl: impersonation active');

      await this.runPostInitialization(accessToken, displayName, email);

      // Clean up URL by removing impersonation params
      parameters.delete('impersonationToken');
      parameters.delete('impersonationRefreshToken');
      parameters.delete('returnToken');
      parameters.delete('impersonationReturnToken');

      const cleaned = `${url.pathname}${parameters.toString() ? `?${parameters.toString()}` : ''}${url.hash}`;
      this.logger.debug(`[ImpersonationService] initializeFromUrl: navigating to cleaned URL ${cleaned}`);
      await this.navigationService.navigateByUrl(cleaned, { extras: { replaceUrl: true } });
      this.logger.debug('[ImpersonationService] initializeFromUrl: completed successfully.');
      return true;
    } catch (error) {
      const details = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      this.logger.warn(`[ImpersonationService] Failed to initialize from URL. Details: ${details}`);
      return false;
    }
  }

  /**
   * Checks if the current object is active.
   *
   * @returns {boolean} True if the object is active, false otherwise.
   */
  public isActive(): boolean {
    return this.active();
  }

  /**
   * Determines whether external authentication should be bypassed based on the current active state
   * and whether the user is impersonated.
   *
   * @returns {boolean} True if external authentication should be bypassed, otherwise false.
   */
  public shouldBypassExternalAuth(): boolean {
    if (this.active()) {
      return true;
    }
    // Fixes AML-DESK-7Z: gate .get() behind .has() because StorageService.get()
    // reports non-Error values (DOMException, SyntaxError) to Sentry when the
    // underlying localStorage access fails, whereas .has() swallows silently.
    if (!this.storage.has(StorageServiceKeysMap.IsImpersonated)) {
      return false;
    }
    return this.storage.get<boolean>(StorageServiceKeysMap.IsImpersonated) === true;
  }

  /**
   * Builds the URL that should be opened to restore the administrator session in AML Admin.
   *
   * @param {string} [impersonatedAccessToken] - The impersonated session's access token, forwarded so admin-desk
   * can authenticate the return as that session.
   * @returns {string | null} The fully qualified URL containing the return token query parameter or null when the URL cannot be built.
   */
  public buildAdminReturnUrl(impersonatedAccessToken?: string): string | null {
    const returnToken = this.impersonatedReturnToken();
    const adminDeskBaseUrl = this.environmentService.adminDeskBaseUrl;

    if (!returnToken || !adminDeskBaseUrl) {
      return null;
    }

    try {
      const targetUrl = new URL(adminDeskBaseUrl);
      const sanitizedPath = targetUrl.pathname.replace(/\/$/, '');
      targetUrl.pathname = `${sanitizedPath}/auth/impersonation-return`;
      targetUrl.searchParams.set('returnToken', returnToken);
      // The backend only accepts the return when it is authenticated as the impersonated session.
      if (impersonatedAccessToken) {
        targetUrl.searchParams.set('impersonationToken', impersonatedAccessToken);
      }
      return targetUrl.href;
    } catch (error) {
      const details = error instanceof Error ? error.message : String(error);
      this.logger.warn(`[ImpersonationService] Failed to build admin return URL. Details: ${details}`);
      // eslint-disable-next-line unicorn/no-null
      return null;
    }
  }

  /**
   * Resets the current state by clearing all related data and context.
   * This includes setting the active state to false, removing the impersonated user's display name and email, and clearing any persisted context.
   *
   * @returns {void} This method does not return a value.
   */
  public clear(): void {
    this.active.set(false);
    this.impersonatedDisplayName.set('');
    this.impersonatedEmail.set('');
    this.impersonatedReturnToken.set('');
    this.clearPersistedContext();
  }

  /**
   * Rehydrates the impersonation context from storage if previous impersonation data is available and valid.
   * Ensures that the impersonation data is correctly loaded and sets the active state accordingly.
   * Clears persisted context if conditions for rehydration are not met or if required data is missing.
   *
   * @returns {void} Does not return a value.
   */
  private rehydrateFromStorage(): void {
    this.logger.debug('[ImpersonationService] rehydrateFromStorage: attempting to restore impersonation context.');
    const storedContext = this.storage.get<StoredImpersonationContext>(StorageServiceKeysMap.ImpersonationContext);
    const isImpersonated = this.storage.get<boolean>(StorageServiceKeysMap.IsImpersonated);
    if (!isImpersonated || !storedContext?.impersonationActive) {
      this.logger.debug(
        '[ImpersonationService] rehydrateFromStorage: no active impersonation found; clearing context.',
      );
      this.active.set(false);
      this.impersonatedDisplayName.set('');
      this.impersonatedEmail.set('');
      this.clearPersistedContext();
      return;
    }

    const kerberosToken = this.storage.get<string>(StorageServiceKeysMap.AccessTokenKerberos);
    if (!kerberosToken) {
      this.logger.debug(
        '[ImpersonationService] rehydrateFromStorage: impersonation flag set but Kerberos token missing; clearing context.',
      );
      this.clearPersistedContext();
      return;
    }

    this.impersonatedDisplayName.set(storedContext.displayName ?? '');
    this.impersonatedEmail.set(storedContext.email ?? '');
    this.impersonatedReturnToken.set(storedContext.returnToken ?? '');
    this.active.set(true);
    this.storage.set(StorageServiceKeysMap.IsImpersonated, true);
    this.logger.debug('[ImpersonationService] rehydrateFromStorage: impersonation session restored from storage.');
  }

  /**
   * Persists the provided impersonation context in storage.
   *
   * @param {StoredImpersonationContext} context - The impersonation context to be stored.
   * @returns {void} This method does not return a value.
   */
  private persistContext(context: StoredImpersonationContext): void {
    this.storage.set(StorageServiceKeysMap.ImpersonationContext, context);
  }

  /**
   * Clears the persisted impersonation context from the storage.
   * Specifically, it removes the `ImpersonationContext` and `IsImpersonated`
   * entries if they exist in the storage service.
   *
   * @returns {void} This method does not return a value.
   */
  private clearPersistedContext(): void {
    if (this.storage.has(StorageServiceKeysMap.ImpersonationContext)) {
      this.storage.remove(StorageServiceKeysMap.ImpersonationContext);
    }
    if (this.storage.has(StorageServiceKeysMap.IsImpersonated)) {
      this.storage.remove(StorageServiceKeysMap.IsImpersonated);
    }
  }

  /**
   * Executes post-initialization tasks for user impersonation, including setting up the user session
   * and navigating to the intended URL.
   *
   * @param {string} accessToken - The access token used to derive user claims for impersonation.
   * @param {string} displayName - The display name of the impersonated user.
   * @param {string} email - The email address of the impersonated user.
   * @returns {Promise<void>} A promise that resolves once the post-initialization tasks are completed.
   */
  private async runPostInitialization(accessToken: string, displayName: string, email: string): Promise<void> {
    this.logger.debug('[ImpersonationService] runPostInitialization: deriving impersonated user from token.');
    const impersonatedUser = this.buildUserFromToken(accessToken, displayName, email);
    if (!impersonatedUser) {
      this.logger.warn('[ImpersonationService] Unable to derive user claims for impersonation session.');
      return;
    }

    await performCommonTasks(this.authFacade, impersonatedUser, this.userProfileFacade);
    this.logger.debug('[ImpersonationService] runPostInitialization: common tasks completed.');

    const resolvedDisplayName = `${impersonatedUser.firstName ?? ''} ${impersonatedUser.lastName ?? ''}`
      .trim()
      .replaceAll(/\s+/g, ' ');
    const fallbackDisplayName = resolvedDisplayName || displayName;
    const resolvedEmail = impersonatedUser.email || email;

    if (fallbackDisplayName && fallbackDisplayName !== this.impersonatedDisplayName()) {
      this.impersonatedDisplayName.set(fallbackDisplayName);
    }
    if (resolvedEmail && resolvedEmail !== this.impersonatedEmail()) {
      this.impersonatedEmail.set(resolvedEmail);
    }

    const updatedContext: StoredImpersonationContext = {
      impersonationActive: true,
      displayName: this.impersonatedDisplayName() || undefined,
      email: this.impersonatedEmail() || undefined,
      returnToken: this.impersonatedReturnToken() || undefined,
    };
    this.persistContext(updatedContext);

    const isNavigated = await this.deepLinkService.navigateToIntendedUrl();
    this.logger.debug(
      `[ImpersonationService] runPostInitialization: attempted deep link navigation -> ${isNavigated}.`,
    );
  }

  /**
   * Builds a User object from the provided access token and additional user information.
   *
   * @param {string} accessToken The access token containing user claims.
   * @param {string} displayName The display name of the user, used as a fallback for name resolution.
   * @param {string} email The email of the user, used as a fallback for email resolution.
   * @returns {User | null} The constructed User object if successful, or null if the required data is incomplete or an error occurs.
   */
  private buildUserFromToken(accessToken: string, displayName: string, email: string): User | null {
    try {
      const claims = jwtDecode<Partial<AdpPlatformTokenClaims> & Record<string, unknown>>(accessToken) ?? {};

      const user: User = {
        uid: claims.oid ?? claims.sub ?? '',
        email: this.resolveEmail(claims, email),
        firstName: this.resolveFirstName(claims, displayName),
        lastName: this.resolveLastName(claims, displayName),
        gender: this.resolveGender(claims),
        preferredLanguage: this.resolveLanguage(claims),
        industry: this.resolveIndustry(claims),
      };

      if (!user.uid || !user.email) {
        return null;
      }

      return user;
    } catch {
      return null;
    }
  }

  /**
   * Resolves the primary email address from the provided claims or fallback.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - The set of user claims from which the email is extracted. The claims may include an `emails` array or a `mail` property.
   * @param {string} fallback - A default email address to be used if no email is found in the claims.
   * @returns {string} The resolved email address, which can be either the first email from the `emails` array, the `mail` property from the claims, or the fallback value. If none are available, an empty string is returned.
   */
  private resolveEmail(claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>, fallback: string): string {
    const emailFromEmailsClaim = Array.isArray(claims.emails) ? claims.emails[0] : undefined;
    const emailFromAdp = claims.mail;
    return emailFromEmailsClaim ?? emailFromAdp ?? fallback ?? '';
  }

  /**
   * Resolves and returns the first name of a user based on the provided claims and display name.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - The claims object which may contain information about the user's first name.
   * @param {string} displayName - The display name from which the first name can be extracted if it is not available in claims.
   * @returns {string} The resolved first name of the user.
   */
  private resolveFirstName(
    claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>,
    displayName: string,
  ): string {
    if (typeof claims.given_name === 'string' && claims.given_name) {
      return claims.given_name;
    }
    const [first = ''] = this.splitDisplayName(displayName || claims.name! || '');
    return first;
  }

  /**
   * Resolves and returns the last name of a user based on the provided claims or display name.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - A partial set of user token claims and additional properties.
   * @param {string} displayName - The display name of the user, used as a fallback to determine the last name.
   * @returns {string} The resolved last name of the user. If no last name is found, returns an empty string.
   */
  private resolveLastName(
    claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>,
    displayName: string,
  ): string {
    if (typeof claims.family_name === 'string' && claims.family_name) {
      return claims.family_name;
    }
    const [, last = ''] = this.splitDisplayName(displayName || claims.name! || '');
    return last;
  }

  /**
   * Splits a full display name into two parts: the first name and the remainder.
   *
   * @param {string} value - The full name to be split. It may include multiple words.
   * @returns {[string, string]} A tuple where the first element is the first name, and the second element is the remainder of the name after the first word. Both elements will be empty strings if the input is falsy.
   */
  private splitDisplayName(value: string): [string, string] {
    if (!value) {
      return ['', ''];
    }
    const parts = value.trim().split(/\s+/);
    if (parts.length === 1) {
      return [parts[0], ''];
    }
    const [first, ...rest] = parts;
    return [first, rest.join(' ')];
  }

  /**
   * Resolves the gender based on the user's token claims.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - A partial set of user token claims which may include gender information.
   * @returns {Gender} The resolved gender value. Defaults to Gender.Masculine if the gender claim is invalid or missing.
   */
  private resolveGender(claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>): Gender {
    const genderClaim = claims.extension_gender;
    if (typeof genderClaim === 'string' && Object.values(Gender).includes(genderClaim)) {
      return genderClaim;
    }
    return Gender.Masculine;
  }

  /**
   * Resolves the preferred language from the given user token claims.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - The user token claims which may include language preferences.
   * @returns {Language} The resolved language based on the input claims or a default language if no valid preference is found.
   */
  private resolveLanguage(claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>): Language {
    const languageClaim = claims.extension_preferredLanguage;
    if (typeof languageClaim === 'string' && Object.values(LanguageMap).includes(languageClaim)) {
      return languageClaim;
    }
    return LanguageMap.English;
  }

  /**
   * Resolves the industry based on the provided user token claims.
   *
   * @param {Partial<AdpPlatformTokenClaims> & Record<string, unknown>} claims - The user token claims that may contain the 'extension_industry' property.
   * @returns {Industry} The resolved industry. If the 'extension_industry' claim is valid, it is returned; otherwise, the default industry is returned.
   */
  private resolveIndustry(claims: Partial<AdpPlatformTokenClaims> & Record<string, unknown>): Industry {
    const industryClaim = claims.extension_industry;
    if (typeof industryClaim === 'string' && Object.values(Industry).includes(industryClaim)) {
      return industryClaim;
    }
    return Industry.RealEstate;
  }

  private get authFacade(): AuthFacade {
    return this.injector.get(AuthFacade);
  }
}
